import { isEventClosed } from "@/lib/eventChange";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QueryErrorCard } from "@/components/ui/QueryErrorCard";
import { MapPin, Clock, CheckCircle2, Loader2, Navigation, Eye, Camera, AlertTriangle } from "lucide-react";
import { isPast, isFuture, addMinutes } from "date-fns";
import { getCentralTimeDate, formatInCentralTime } from "@/utils/timezone";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useState, useEffect, useMemo } from "react";
import { CheckInOptionsDialog, CheckInReason } from "./CheckInOptionsDialog";
import { ViewSubmittedSelfieDialog } from "./ViewSubmittedSelfieDialog";
import { SelfieCheckInDialog } from "./SelfieCheckInDialog";
import { useLocationPermission } from "@/hooks/useLocationPermission";
import { useRefreshState } from "@/contexts/RefreshContext";
import { cn } from "@/lib/utils";
import { LocationDeniedCard } from "@/components/permissions/LocationDeniedCard";

interface TodayCheckInCardProps {
  userId: string;
  autoOpenEventId?: string | null;
}

interface EventWithDistance {
  id: string;
  title: string;
  date: string;
  location: string;
  lat: number | null;
  lng: number | null;
  host_name: string;
  category: string;
  duration_min: number | null;
  distance?: number;
  selfie_review_status?: string | null;
}

const DISTANCE_BUFFER_MILES = 5;
const GRACE_PERIOD_MINUTES = 120;
const EARLY_CHECK_IN_MINUTES = 60;

const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 3958.8; // Earth radius in miles
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

type CheckInState = {
  canCheckIn: boolean;
  reason: CheckInReason;
  message: string;
  showLocationButton: boolean;
  showSelfieButton: boolean;
  isLateCheckIn: boolean;
};

const getCheckInState = (
  event: EventWithDistance,
  userLocation: { lat: number; lng: number } | null,
  isLocationDenied: boolean,
  isLocationLoading: boolean = false,
  isLocationGranted: boolean = false
): CheckInState => {
  // Use Central Time for event date comparisons
  const eventDate = getCentralTimeDate(event.date);
  // Create a comparable Date object from the Central Time values
  const eventDateComparable = new Date(
    eventDate.getUTCFullYear(),
    eventDate.getUTCMonth(),
    eventDate.getUTCDate(),
    eventDate.getUTCHours(),
    eventDate.getUTCMinutes(),
    eventDate.getUTCSeconds()
  );
  const now = new Date();
  const durationMin = event.duration_min || 60;
  const eventEndTime = addMinutes(eventDateComparable, durationMin);
  const checkInWindowStart = addMinutes(eventDateComparable, -EARLY_CHECK_IN_MINUTES);
  const checkInWindowEnd = addMinutes(eventEndTime, GRACE_PERIOD_MINUTES);
  
  // Check-in window hasn't opened yet (1 hour before event)
  if (isFuture(checkInWindowStart)) {
    return {
      canCheckIn: false,
      reason: "event_not_started",
      message: `Check-in opens at ${formatInCentralTime(addMinutes(new Date(event.date), -EARLY_CHECK_IN_MINUTES).toISOString(), "h:mm a")}`,
      showLocationButton: false,
      showSelfieButton: true,
      isLateCheckIn: false,
    };
  }
  
  // Check-in window has closed (late check-in)
  if (isPast(checkInWindowEnd)) {
    return {
      canCheckIn: false,
      reason: "event_ended",
      message: "Check-in window closed • Submit selfie for review",
      showLocationButton: false,
      showSelfieButton: true,
      isLateCheckIn: true,
    };
  }
  
  // Location denied
  if (isLocationDenied) {
    return {
      canCheckIn: false,
      reason: "location_denied",
      message: "Location access denied",
      showLocationButton: true,
      showSelfieButton: true,
      isLateCheckIn: false,
    };
  }
  
  // Location is being fetched
  if (isLocationLoading) {
    return {
      canCheckIn: false,
      reason: "location_loading",
      message: "Getting your location...",
      showLocationButton: false,
      showSelfieButton: false,
      isLateCheckIn: false,
    };
  }
  
  // No location data yet
  if (!userLocation) {
    // If permission is granted but no coordinates, GPS is just slow
    // Don't show "Enable location" button in this case
    return {
      canCheckIn: false,
      reason: "no_location_data",
      message: isLocationGranted 
        ? "Getting your location..." 
        : "Enable location for automatic check-in",
      showLocationButton: !isLocationGranted,
      showSelfieButton: true,
      isLateCheckIn: false,
    };
  }
  
  // Event doesn't have coordinates
  if (!event.lat || !event.lng) {
    return {
      canCheckIn: false,
      reason: "no_location_data",
      message: "Event location not available",
      showLocationButton: false,
      showSelfieButton: true,
      isLateCheckIn: false,
    };
  }
  
  // Calculate distance
  const distance = calculateDistance(
    userLocation.lat,
    userLocation.lng,
    event.lat,
    event.lng
  );
  
  // Too far from event
  if (distance > DISTANCE_BUFFER_MILES) {
    return {
      canCheckIn: false,
      reason: "too_far",
      message: `${distance.toFixed(1)} mi away • Get closer or submit selfie`,
      showLocationButton: true,
      showSelfieButton: true,
      isLateCheckIn: false,
    };
  }
  
  // Within range - can check in!
  return {
    canCheckIn: true,
    reason: "default",
    message: `${distance.toFixed(1)} mi away • Ready to check in!`,
    showLocationButton: true,
    showSelfieButton: false,
    isLateCheckIn: false,
  };
};

export const TodayCheckInCard = ({ userId, autoOpenEventId }: TodayCheckInCardProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status: locationStatus, isGranted, isDenied, isLoading: isPermissionLoading, requestPermission, getCurrentPosition, openAppSettings } = useLocationPermission();
  const { isRefreshing } = useRefreshState();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const [selectedEventForOptions, setSelectedEventForOptions] = useState<{ 
    id: string; 
    title: string;
    reason: CheckInReason;
    distance?: number;
    eventStartTime?: Date;
  } | null>(null);
  const [viewSelfieDialogOpen, setViewSelfieDialogOpen] = useState(false);
  const [selectedEventForSelfieView, setSelectedEventForSelfieView] = useState<{ id: string; title: string } | null>(null);
  const [selfieDialogOpen, setSelfieDialogOpen] = useState(false);
  const [selectedEventForSelfie, setSelectedEventForSelfie] = useState<{ 
    id: string; 
    title: string;
    reason?: CheckInReason;
    distance?: number;
  } | null>(null);

  // Fetch today's RSVP'd events that haven't been attended yet
  const { data: todayEvents, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ['today-checkin-events', userId],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const { data, error } = await supabase
        .from('event_attendances')
        .select(`
          event_id,
          status,
          selfie_review_status,
          events (
            id,
            title,
            date,
            location,
            lat,
            lng,
            host_name,
            category,
            duration_min,
            lifecycle_status
          )
        `)
        .eq('user_id', userId)
        .eq('status', 'rsvp')
        .gte('events.date', today.toISOString())
        .lt('events.date', tomorrow.toISOString());

      if (error) throw error;

      return (data
        ?.map((item: any) => item.events ? ({
          ...item.events,
          selfie_review_status: item.selfie_review_status,
        }) : null)
        .filter((event): event is EventWithDistance => event !== null && event.date !== null && !isEventClosed(event as EventWithDistance & { lifecycle_status?: string })) as EventWithDistance[]) || [];
    },
    enabled: !!userId,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Handle retry for errors
  const handleRetry = () => {
    toast.info("Retrying to load today's events...");
    refetch();
  };

  // Auto-fetch location when permission is granted (retry if location is null)
  useEffect(() => {
    const fetchLocation = async () => {
      if (isGranted && !isGettingLocation) {
        setIsGettingLocation(true);
        const position = await getCurrentPosition();
        if (position) {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        }
        setIsGettingLocation(false);
      }
    };
    
    // Only fetch if we don't have a location yet
    if (!userLocation) {
      fetchLocation();
    }
  }, [isGranted, getCurrentPosition]); // Removed userLocation from deps to prevent infinite loop

  // Auto-open check-in dialog when deep linked from notification
  useEffect(() => {
    if (autoOpenEventId && todayEvents) {
      const event = todayEvents.find(e => e.id === autoOpenEventId);
      if (event) {
        const state = getCheckInState(event, userLocation, isDenied, isGettingLocation, isGranted);
        const centralDate = getCentralTimeDate(event.date);
        const eventStartTime = new Date(
          centralDate.getUTCFullYear(),
          centralDate.getUTCMonth(),
          centralDate.getUTCDate(),
          centralDate.getUTCHours(),
          centralDate.getUTCMinutes()
        );
        setSelectedEventForOptions({ 
          id: event.id, 
          title: event.title,
          reason: state.reason,
          distance: event.distance,
          eventStartTime,
        });
        setOptionsDialogOpen(true);
      }
    }
  }, [autoOpenEventId, todayEvents, userLocation, isDenied]);

  // Handle requesting location permission
  const handleRequestLocation = async () => {
    setIsGettingLocation(true);
    const granted = await requestPermission();
    if (granted) {
      const position = await getCurrentPosition();
      if (position) {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      }
    }
    setIsGettingLocation(false);
  };

  // Calculate distances when user location is available
  const eventsWithDistance = useMemo(() => {
    return todayEvents?.map((event) => {
      if (!event.lat || !event.lng || !userLocation) {
        return { ...event, distance: undefined };
      }
      const distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        event.lat,
        event.lng
      );
      return { ...event, distance };
    });
  }, [todayEvents, userLocation]);

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationKey: ["geo-check-in"],
    mutationFn: async (eventId: string) => {
      if (!userLocation) {
        throw new Error("Location not available");
      }

      const { data, error } = await supabase.functions.invoke(
        'verify-event-attendance',
        {
          body: {
            eventId,
            userLatitude: userLocation.lat,
            userLongitude: userLocation.lng,
          },
        }
      );

      if (error) throw error;
      if (!data.verified) {
        // Handle structured error response
        const errorData = data as { verified: false; reason: string; code?: string; distance?: number };
        throw new Error(errorData.reason || "Check-in failed");
      }
      
      return data;
    },
    onSuccess: (data) => {
      toast.success("Checked in!", {
        description: data.message,
      });
      queryClient.invalidateQueries({ queryKey: ['today-checkin-events'] });
      queryClient.invalidateQueries({ queryKey: ['event-attendance'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['daily-activity'] });
    },
    onError: (error: Error) => {
      toast.error("Check-in failed", {
        description: error.message,
      });
    },
  });

  const handleCheckInClick = (event: EventWithDistance, e: React.MouseEvent) => {
    e.stopPropagation();
    const state = getCheckInState(event, userLocation, isDenied, isGettingLocation, isGranted);
    
    if (state.canCheckIn) {
      // Direct check-in
      checkInMutation.mutate(event.id);
    } else {
      // Show options dialog with context
      const centralDate = getCentralTimeDate(event.date);
      const eventStartTime = new Date(
        centralDate.getUTCFullYear(),
        centralDate.getUTCMonth(),
        centralDate.getUTCDate(),
        centralDate.getUTCHours(),
        centralDate.getUTCMinutes()
      );
      setSelectedEventForOptions({
        id: event.id,
        title: event.title,
        reason: state.reason,
        distance: event.distance,
        eventStartTime,
      });
      setOptionsDialogOpen(true);
    }
  };

  const handleQuickSelfie = (event: EventWithDistance, e: React.MouseEvent) => {
    e.stopPropagation();
    const state = getCheckInState(event, userLocation, isDenied, isGettingLocation, isGranted);
    setSelectedEventForSelfie({ 
      id: event.id, 
      title: event.title,
      reason: state.reason,
      distance: event.distance,
    });
    setSelfieDialogOpen(true);
  };

  if (isError) {
    return (
      <QueryErrorCard
        title="Unable to load check-ins"
        description="We had trouble fetching today's events."
        onRetry={handleRetry}
        isRetrying={isRefetching}
      />
    );
  }

  if (isLoading || !todayEvents || todayEvents.length === 0) {
    return null;
  }

  return (
    <div className={cn(
      "space-y-4 transition-all duration-300 ease-out",
      isRefreshing && "opacity-70 scale-[0.98]"
    )}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Check In Today</h2>
        {/* Show loading state while checking permissions */}
        {isPermissionLoading && (
          <Button
            size="sm"
            variant="ghost"
            disabled
          >
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
            Checking...
          </Button>
        )}
        {/* Show "Enable Location" only when permission has never been asked (prompt state) */}
        {!isPermissionLoading && locationStatus === "prompt" && !isGettingLocation && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRequestLocation}
            disabled={isGettingLocation}
          >
            <Navigation className="h-4 w-4 mr-1" />
            Enable Location
          </Button>
        )}
        {/* Show "Refresh" when permission check complete, granted but no location data yet */}
        {!isPermissionLoading && isGranted && !userLocation && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRequestLocation}
            disabled={isGettingLocation}
          >
            {isGettingLocation ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Navigation className="h-4 w-4 mr-1" />
            )}
            {isGettingLocation ? "Getting..." : "Refresh Location"}
          </Button>
        )}
      </div>

      {isDenied && (
        <LocationDeniedCard onOpenSettings={openAppSettings} compact />
      )}

      <div className="space-y-3">
        {eventsWithDistance?.map((event) => {
          const state = getCheckInState(event, userLocation, isDenied, isGettingLocation, isGranted);
          const selfieUnderReview = event.selfie_review_status === 'pending';

          return (
            <Card
              key={event.id}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/events/${event.id}`)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-start gap-2 flex-wrap">
                    <h3 className="font-semibold line-clamp-1">{event.title}</h3>
                    <Badge variant="secondary" className="shrink-0">
                      {event.category}
                    </Badge>
                    {selfieUnderReview && (
                      <Badge 
                        variant="outline" 
                        className="shrink-0 cursor-pointer hover:bg-muted"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEventForSelfieView({ id: event.id, title: event.title });
                          setViewSelfieDialogOpen(true);
                        }}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Selfie Under Review ⏳
                      </Badge>
                    )}
                    {state.isLateCheckIn && !selfieUnderReview && (
                      <Badge variant="outline" className="shrink-0 text-amber-600 border-amber-300">
                        <Clock className="w-3 h-3 mr-1" />
                        Late Check-In
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {event.date && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatInCentralTime(event.date, "h:mm a")}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {event.distance !== undefined
                        ? `${event.distance.toFixed(1)} mi away`
                        : event.location}
                    </div>
                  </div>

                  {/* Status message */}
                  {!selfieUnderReview && !state.canCheckIn && (
                    <div className={cn(
                      "text-xs flex items-center gap-1",
                      state.reason === "too_far" && "text-amber-600",
                      state.reason === "location_denied" && "text-destructive",
                      state.reason === "event_ended" && "text-muted-foreground",
                      state.reason === "event_not_started" && "text-blue-600",
                      state.reason === "location_loading" && "text-muted-foreground",
                    )}>
                      {state.reason === "too_far" && <MapPin className="h-3 w-3" />}
                      {state.reason === "location_denied" && <AlertTriangle className="h-3 w-3" />}
                      {state.reason === "event_ended" && <Clock className="h-3 w-3" />}
                      {state.reason === "event_not_started" && <Clock className="h-3 w-3" />}
                      {state.reason === "location_loading" && <Loader2 className="h-3 w-3 animate-spin" />}
                      {state.message}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {selfieUnderReview ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEventForSelfieView({ id: event.id, title: event.title });
                        setViewSelfieDialogOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                  ) : state.canCheckIn ? (
                    <Button
                      size="sm"
                      disabled={checkInMutation.isPending}
                      onClick={(e) => handleCheckInClick(event, e)}
                    >
                      {checkInMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Check In
                        </>
                      )}
                    </Button>
                  ) : state.reason === "location_loading" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled
                    >
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Locating...
                    </Button>
                  ) : state.isLateCheckIn ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => handleQuickSelfie(event, e)}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Selfie
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => handleCheckInClick(event, e)}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Check In
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {selectedEventForOptions && (
        <CheckInOptionsDialog
          open={optionsDialogOpen}
          onOpenChange={setOptionsDialogOpen}
          eventId={selectedEventForOptions.id}
          eventTitle={selectedEventForOptions.title}
          reason={selectedEventForOptions.reason}
          distance={selectedEventForOptions.distance}
          eventStartTime={selectedEventForOptions.eventStartTime}
        />
      )}

      {selectedEventForSelfieView && (
        <ViewSubmittedSelfieDialog
          open={viewSelfieDialogOpen}
          onOpenChange={setViewSelfieDialogOpen}
          eventId={selectedEventForSelfieView.id}
          eventTitle={selectedEventForSelfieView.title}
        />
      )}

      {selectedEventForSelfie && (
        <SelfieCheckInDialog
          open={selfieDialogOpen}
          onOpenChange={setSelfieDialogOpen}
          eventId={selectedEventForSelfie.id}
          eventTitle={selectedEventForSelfie.title}
          reason={selectedEventForSelfie.reason}
          distance={selectedEventForSelfie.distance}
        />
      )}
    </div>
  );
};
